import type { BadgeProps } from "@alpacahq/ui/old";

import type {
  AlpacaTransferStatus,
  DtccAccountType,
  DtccTransferType,
} from "~/v2/api/rest/acats";

/**
 * Badge variants for ACATS transfer status display
 * Used in transfer history tables and detail modals
 */
// Keyed by `string` so unknown backend statuses (accepted by the widened schema)
// look up as `undefined` instead of failing the typechecker.
export const ACATS_TRANSFER_STATUS_BADGES: Record<
  string,
  { label: string; variant: BadgeProps["variant"] }
> = {
  PENDING: { variant: "v2/yellow", label: "Pending" },
  IN_PROGRESS: { variant: "v2/yellow", label: "In Progress" },
  REJECTED: { variant: "v2/red", label: "Rejected" },
  SETTLED: { variant: "v2/emerald", label: "Completed" },
} satisfies Record<
  AlpacaTransferStatus,
  { label: string; variant: BadgeProps["variant"] }
>;

/**
 * Mapping of DTCC transfer types to human-readable labels
 */
export const ACATS_TRANSFER_TYPE_MAP: Record<DtccTransferType, string> = {
  FUL: "Full Transfer",
  PTF: "Full Transfer", // Position Transfer Fund
  MFC: "Full Transfer", // Mutual Fund Cleanup
  PTD: "Partial Transfer", // Partial Transfer (Deliverer)
  PTR: "Partial Transfer", // Partial Transfer (Receiver)
  FRV: "Fail Reversal",
  RCL: "Reclaim",
  RCR: "Residual Credit",
};

/**
 * Mapping of DTCC account types to human-readable labels
 * Used in transfer history tables and detail modals
 */
export const ACATS_ACCOUNT_TYPE_MAP: Record<DtccAccountType, string> = {
  AGENCY: "Agency Account",
  BANK_CUSTODY: "Bank Custody",
  BENEFICIARY: "Beneficiary Account",
  BENEFICIARY_ROTH_IRA: "Beneficiary Roth IRA",
  CORPORATE: "Corporate Account",
  CO_TRUSTEE: "Co-Trustee Account",
  COVERDELL_IRA: "Coverdell IRA",
  CUSTODIAN_UGMA: "Custodian UGMA",
  DIRECT_ROLLOVER: "Direct Rollover",
  ESTATE: "Estate Account",
  HSA: "Health Savings Account",
  IRA: "Traditional IRA",
  JOINT: "Joint Account",
  MEDICAL_SAVINGS_ACCOUNT: "Medical Savings Account",
  OTHER: "Other",
  QUALIFIED_OR_PROFIT_SHARING_OR_PENSION: "Qualified/Profit Sharing/Pension",
  ROTH_401K: "Roth 401(k)",
  ROTH_IRA: "Roth IRA",
  ROTH_SEP: "Roth SEP-IRA",
  ROTH_SIMPLE: "Roth SIMPLE IRA",
  ROTH_SOLO_401K: "Roth Solo 401(k)",
  SARSEP: "SARSEP",
  SEP_IRA: "SEP-IRA",
  SIMPLE_IRA: "SIMPLE IRA",
  SINGLE: "Individual Account",
  SOLO_401K: "Solo 401(k)",
  TRUST: "Trust Account",
  TYPE_401K: "401(k)",
  TYPE_403B: "403(b)",
  TYPE_457_PLAN: "457 Plan",
  TYPE_529_PLAN: "529 Plan",
};
